/**
 * FERP Portal - Root Application Component
 *
 * Implements Three-Layer Interface:
 * - User View (Layer 1): "My work" - tasks, deadlines, actions
 * - Admin View (Layer 2): "Team work" - assignments, queues, metrics
 * - Architect View (Layer 3): "Primitive composition" - 25 building blocks
 *
 * FERP Principles:
 * - Data-driven: Subgraph-specific routes generated from SubgraphMetadata
 * - NO hardcoded subgraph-specific routes (dynamic generation)
 * - Core routes (primitives, dashboard) remain static
 * - LAZY LOADING: All pages load on-demand to reduce initial bundle (DRR-313)
 *
 * CONSTRAINTS:
 * - NO MUI components (Mantine v8.x only)
 * - NO Redux/Zustand/MobX (Apollo Client is single source of truth)
 * - NO hardcoded values (all config from backend/metadata)
 * - NO direct useQuery<Record<string, any>>(all data access through Repository Pattern)
 * - NO client-side ID generation (backend Snowflake IDs only)
 *
 * Route Organization by 25-Primitive Lifecycle:
 * - DESIGN: /architect/workflows, contexts, constraints, dependencies, forms
 * - CONFIGURE: /admin/actors, policies, permissions, queues, schedules
 * - EXECUTE: /tasks, states, events, sagas, dagnodes
 * - MONITOR: /admin/metrics, alerts, audits, reports, notifications
 * - GOVERN: /admin/contracts, compliance, risks, evidence, versions
 */

import React, { Suspense, useEffect, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Center, Loader, Text, Stack } from '@mantine/core';

import { AuthProvider, useAuth } from './auth/AuthContext';
import { SimulationProvider } from './auth/SimulationContext';
import { registerDefaultTabRenderers } from './components/entity-detail/registerDefaultRenderers';

// DRR-165: Register FormSchema-driven tab renderers at app init
registerDefaultTabRenderers();

// DRR-321: Register dashboard widgets at app init (side-effect imports)
import './widgets/coreWidgets';
import '@ferp/subgraph-fhir/widgets';

// useMyViewLayerQuery moved to NavigationContext (DRR-313)
import { ActorNameProvider } from './contexts/ActorNameContext';
import { FhirLabelsProvider } from './contexts/FhirLabelsContext';
import { NavigationProvider } from './contexts/NavigationContext';
import { ViewContextProvider } from './contexts/ViewContext';
import { MainLayout } from './layouts/MainLayout';
import { PageErrorBoundary } from './components/PageErrorBoundary';

// ============================================================================
// EAGER IMPORTS - Auth-critical pages that must load immediately
// ============================================================================
import { LoginPage } from './pages/LoginPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';

// Route Guards (must be eager for auth flow)
import { RequireArchitect } from './components/guards';

// Admin Components (used in layout, not routes)
import { QueueManager } from './components/admin';

// Architect Components (used in layout)
import { PrimitiveGrid, SubgraphConstellation } from './components/architect';

// Terminology translation (providers must be eager)
import { TerminologyProvider } from '@ferp/workflow-forms';
// TenantBranding type removed — using terminologyMap directly (DRR-189)
import { useAdaptiveTenantBranding } from './hooks/useAdaptiveTenantBranding';
import { useResolvedTerminology } from './hooks/useResolvedTerminology';

// DATA-DRIVEN: Dynamic routing from SubgraphMetadata
import { useTenantSubgraphConfig } from './hooks/useTenantSubgraphConfig';
import { useDynamicRoutes } from './components/routing';

// ============================================================================
// LAZY IMPORTS - All pages load on-demand (DRR-313: reduce initial bundle)
// ============================================================================

// Core Pages
const DashboardPage = React.lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const SubjectDashboardPage = React.lazy(() => import('./pages/subject-dashboard').then(m => ({ default: m.SubjectDashboardPage })));
const WorkSurfacePageConnected = React.lazy(() => import('./pages/work-surface').then(m => ({ default: m.WorkSurfacePageConnected })));

// DESIGN Phase Primitives (Architect Layer)
const WorkflowListPage = React.lazy(() => import('./pages/workflows').then(m => ({ default: m.WorkflowListPage })));
const WorkflowRegistryPage = React.lazy(() => import('./pages/workflows').then(m => ({ default: m.WorkflowRegistryPage })));
const LlmWorkflowEditorPage = React.lazy(() => import('./pages/workflows').then(m => ({ default: m.LlmWorkflowEditorPage })));
const MasterWorkflowPage = React.lazy(() => import('./pages/workflows').then(m => ({ default: m.MasterWorkflowPage })));
const WorkflowDetailPage = React.lazy(() => import('./pages/workflows').then(m => ({ default: m.WorkflowDetailPage })));
const WorkflowStudioPage = React.lazy(() => import('./pages/workflows').then(m => ({ default: m.WorkflowStudioPage })));
const WorkflowVisualizerPage = React.lazy(() => import('./pages/workflows/WorkflowVisualizerPage'));
const LlmFormSchemaEditorPage = React.lazy(() => import('./pages/forms').then(m => ({ default: m.LlmFormSchemaEditorPage })));
const FormGalleryPage = React.lazy(() => import('./pages/forms').then(m => ({ default: m.FormGalleryPage })));
const ConstraintListPage = React.lazy(() => import('./pages/constraints').then(m => ({ default: m.ConstraintListPage })));
const DependencyListPage = React.lazy(() => import('./pages/dependencies').then(m => ({ default: m.DependencyListPage })));
const TemplateListPage = React.lazy(() => import('./pages/templates').then(m => ({ default: m.TemplateListPage })));

// CONFIGURE Phase Primitives (Admin Layer) - Large admin import
const FormSchemaAdminPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.FormSchemaAdminPage })));
const PrimitiveConstellationPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.PrimitiveConstellationPage })));
const SourceMapReviewPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.SourceMapReviewPage })));
const SourceMapListPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.SourceMapListPage })));
const ProductCatalogPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.ProductCatalogPage })));
const RoleAccessComparisonPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.RoleAccessComparisonPage })));
const RBACDashboardPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.RBACDashboardPage })));
const WitnessQueuePage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.WitnessQueuePage })));
const SagaMonitoringPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.SagaMonitoringPage })));
const ExecutiveSafetyPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.ExecutiveSafetyPage })));
const CacheManagementPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.CacheManagementPage })));
const CLevelAuditPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.CLevelAuditPage })));
const JobMonitoringPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.JobMonitoringPage })));
const QueueManagerPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.QueueManagerPage })));
const SystemHealthPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.SystemHealthPage })));
const OptimizationPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.OptimizationPage })));
const TaskAnalyticsPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.TaskAnalyticsPage })));
const ProcessAnalyticsPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.ProcessAnalyticsPage })));
const ApprovalQueuePage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.ApprovalQueuePage })));
const EmergencyParticipantPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.EmergencyParticipantPage })));
const SafetyEventsPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.SafetyEventsPage })));
const ValidationReviewPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.ValidationReviewPage })));
const MultiLLMComparisonPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.MultiLLMComparisonPage })));
const PolicyAnalyticsPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.PolicyAnalyticsPage })));
const RegulatoryGovernancePage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.RegulatoryGovernancePage })));
const ProductionMonitoringPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.ProductionMonitoringPage })));
const WorkflowCompositionPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.WorkflowCompositionPage })));
const ActorRuntimePage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.ActorRuntimePage })));
const ActorDescriptorPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.ActorDescriptorPage })));
const DraftManagementPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.DraftManagementPage })));
const AuthExplorerPage = React.lazy(() => import('./pages/admin/AuthExplorerPage').then(m => ({ default: m.AuthExplorerPage })));
const ComprehensiveDashboardPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.ComprehensiveDashboardPage })));
const SecureSystemHealthPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.SecureSystemHealthPage })));
const TabulatorLifecyclePage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.TabulatorLifecyclePage })));
const PrimitiveCompositionPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.PrimitiveCompositionPage })));
const PrimitiveRegistryPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.PrimitiveRegistryPage })));
const DomainConfigPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.DomainConfigPage })));
const ComplianceConfigPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.ComplianceConfigPage })));
const GovernanceDashboardPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.GovernanceDashboardPage })));
const GovernanceContextPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.GovernanceContextPage })));
const SagaMetricsPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.SagaMetricsPage })));
const MonitoringDashboardPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.MonitoringDashboardPage })));
const RoleManagementPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.RoleManagementPage })));
const IntegrationManagementPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.IntegrationManagementPage })));
const ResourceManagementPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.ResourceManagementPage })));
const EscalationManagementPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.EscalationManagementPage })));
const ScheduleManagementPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.ScheduleManagementPage })));
const PolicyManagementPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.PolicyManagementPage })));
const ExpertRoutingPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.ExpertRoutingPage })));
const TaskApprovalPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.TaskApprovalPage })));
const ApprovalPoliciesPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.ApprovalPoliciesPage })));
const TabulatorEntityPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.TabulatorEntityPage })));
const MasterWorkflowDashboardPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.MasterWorkflowDashboardPage })));
const TemplateWorkflowPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.TemplateWorkflowPage })));
const ConversionProgressPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.ConversionProgressPage })));
const ExtractionReviewPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.ExtractionReviewPage })));
const DashboardEnhancedPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.DashboardEnhancedPage })));
const GraphQLWidgetsPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.GraphQLWidgetsPage })));
const LifecycleWidgetsPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.LifecycleWidgetsPage })));
const CompositionEnginePage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.CompositionEnginePage })));
const ComplexProcessPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.ComplexProcessPage })));
const PerformanceOptimizationPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.PerformanceOptimizationPage })));
const ChaosTemplatePage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.ChaosTemplatePage })));
const Sprint7DashboardPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.Sprint7DashboardPage })));
const AmbientDashboardPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.AmbientDashboardPage })));
const QapiDashboardPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.QapiDashboardPage })));
const ComplianceDashboardPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.ComplianceDashboardPage })));
const ProductionSystemHealthPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.ProductionSystemHealthPage })));
const ConfigProposalQueuePage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.ConfigProposalQueuePage })));
const SearchPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.SearchPage })));
const TerminologyPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.TerminologyPage })));
const AuthObservabilityPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.AuthObservabilityPage })));
const EntityTypesPage = React.lazy(() => import('./pages/admin').then(m => ({ default: m.EntityTypesPage })));
const SubgraphEntityManagerPage = React.lazy(() => import('./pages/admin/SubgraphEntityManagerPage'));
const SpatialPinningEditor = React.lazy(() => import('./pages/admin/SpatialPinningEditor').then(m => ({ default: m.SpatialPinningEditor })));

// Policy, Actor, Role, Schedule pages
const PolicyListPage = React.lazy(() => import('./pages/policies').then(m => ({ default: m.PolicyListPage })));
const ActorListPage = React.lazy(() => import('./pages/actors').then(m => ({ default: m.ActorListPage })));
const ActorManagementPage = React.lazy(() => import('./pages/actors').then(m => ({ default: m.ActorManagementPage })));
const RoleListPage = React.lazy(() => import('./pages/roles').then(m => ({ default: m.RoleListPage })));
const ScheduleListPage = React.lazy(() => import('./pages/schedules').then(m => ({ default: m.ScheduleListPage })));

// EXECUTE Phase Primitives (User Layer)
const TaskListPage = React.lazy(() => import('./pages/tasks').then(m => ({ default: m.TaskListPage })));
const TaskDetailPage = React.lazy(() => import('./pages/tasks').then(m => ({ default: m.TaskDetailPage })));
const TaskTemplatesPage = React.lazy(() => import('./pages/tasks').then(m => ({ default: m.TaskTemplatesPage })));
const TaskAssignmentsPage = React.lazy(() => import('./pages/task-assignments').then(m => ({ default: m.TaskAssignmentsPage })));
const OverdueTasksPage = React.lazy(() => import('./pages/overdue-tasks').then(m => ({ default: m.OverdueTasksPage })));
const TaskDelegationPage = React.lazy(() => import('./pages/task-delegation').then(m => ({ default: m.TaskDelegationPage })));
const WorkflowStateView = React.lazy(() => import('./pages/workflow-state').then(m => ({ default: m.WorkflowStateView })));
const WorkflowStatePage = React.lazy(() => import('./pages/workflow-state').then(m => ({ default: m.WorkflowStatePage })));
const StateListPage = React.lazy(() => import('./pages/states').then(m => ({ default: m.StateListPage })));
const EventListPage = React.lazy(() => import('./pages/events').then(m => ({ default: m.EventListPage })));
const SagaListPage = React.lazy(() => import('./pages/sagas').then(m => ({ default: m.SagaListPage })));
const DAGNodeListPage = React.lazy(() => import('./pages/dagnodes').then(m => ({ default: m.DAGNodeListPage })));
const InstanceListPage = React.lazy(() => import('./pages/instances').then(m => ({ default: m.InstanceListPage })));
const InstanceDetailPage = React.lazy(() => import('./pages/instances').then(m => ({ default: m.InstanceDetailPage })));

// Hiring Pipeline (DRR-241)
const ApplicantStatusPage = React.lazy(() => import('./pages/hiring/ApplicantStatusPage').then(m => ({ default: m.ApplicantStatusPage })));

// MONITOR Phase Primitives
const MetricListPage = React.lazy(() => import('./pages/metrics').then(m => ({ default: m.MetricListPage })));
const AlertListPage = React.lazy(() => import('./pages/alerts').then(m => ({ default: m.AlertListPage })));
const BeaconDashboardPage = React.lazy(() => import('./pages/alerts').then(m => ({ default: m.BeaconDashboardPage })));
const ReportListPage = React.lazy(() => import('./pages/reports').then(m => ({ default: m.ReportListPage })));
const NotificationListPage = React.lazy(() => import('./pages/notifications').then(m => ({ default: m.NotificationListPage })));
const TransactionListPage = React.lazy(() => import('./pages/transactions').then(m => ({ default: m.TransactionListPage })));

// Best Practice Intelligence (DRR-156)
const RecommendationsPage = React.lazy(() => import('./pages/recommendations').then(m => ({ default: m.RecommendationsPage })));

// GOVERN Phase Primitives
const ComplianceListPage = React.lazy(() => import('./pages/compliance').then(m => ({ default: m.ComplianceListPage })));
const ReadinessDashboard = React.lazy(() => import('./pages/compliance').then(m => ({ default: m.ReadinessDashboard })));
const ComplianceEvidencePage = React.lazy(() => import('./pages/compliance').then(m => ({ default: m.ComplianceEvidencePage })));
const PracticeSurveyRunner = React.lazy(() => import('./pages/compliance').then(m => ({ default: m.PracticeSurveyRunner })));
const PhotoComplianceUpload = React.lazy(() => import('./pages/compliance').then(m => ({ default: m.PhotoComplianceUpload })));
const RuleSetViewerPage = React.lazy(() => import('./pages/compliance').then(m => ({ default: m.RuleSetViewerPage })));
const RuleSetAdminPage = React.lazy(() => import('./pages/compliance').then(m => ({ default: m.RuleSetAdminPage })));
const StandardsPackPage = React.lazy(() => import('./pages/compliance').then(m => ({ default: m.StandardsPackPage })));
const AssessmentEvidenceDashboard = React.lazy(() => import('./pages/compliance').then(m => ({ default: m.AssessmentEvidenceDashboard })));
const ComplianceManualPage = React.lazy(() => import('./pages/compliance').then(m => ({ default: m.ComplianceManualPage })));
const AuditFormsPage = React.lazy(() => import('./pages/compliance').then(m => ({ default: m.AuditFormsPage })));
const RiskListPage = React.lazy(() => import('./pages/risks').then(m => ({ default: m.RiskListPage })));
const EvidenceListPage = React.lazy(() => import('./pages/evidence').then(m => ({ default: m.EvidenceListPage })));
const ContractListPage = React.lazy(() => import('./pages/contracts').then(m => ({ default: m.ContractListPage })));
const TraceListPage = React.lazy(() => import('./pages/traces').then(m => ({ default: m.TraceListPage })));
const CheckpointListPage = React.lazy(() => import('./pages/checkpoints').then(m => ({ default: m.CheckpointListPage })));

// OPTIMIZE Phase Primitives
const AuditListPage = React.lazy(() => import('./pages/audits').then(m => ({ default: m.AuditListPage })));
const AnalyticsListPage = React.lazy(() => import('./pages/analytics').then(m => ({ default: m.AnalyticsListPage })));
const ReconciliationDashboardPage = React.lazy(() => import('./pages/reconciliation').then(m => ({ default: m.ReconciliationDashboardPage })));
const ResourceListPage = React.lazy(() => import('./pages/resources').then(m => ({ default: m.ResourceListPage })));
const IntegrationListPage = React.lazy(() => import('./pages/integrations').then(m => ({ default: m.IntegrationListPage })));
const ValidationListPage = React.lazy(() => import('./pages/validations').then(m => ({ default: m.ValidationListPage })));
const QueueListPage = React.lazy(() => import('./pages/queues').then(m => ({ default: m.QueueListPage })));

// Sync Dashboard (DRR-113)
const SyncDashboardPage = React.lazy(() => import('./pages/sync').then(m => ({ default: m.SyncDashboardPage })));

// Config Type List Pages (DRR-125)
const TaskGroupListPage = React.lazy(() => import('./pages/task-groups').then(m => ({ default: m.TaskGroupListPage })));
const TaskGroupDetailPage = React.lazy(() => import('./pages/task-groups').then(m => ({ default: m.TaskGroupDetailPage })));
const TaskGroupProgressPage = React.lazy(() => import('./pages/task-groups').then(m => ({ default: m.TaskGroupProgressPage })));
const CascadeListPage = React.lazy(() => import('./pages/cascades').then(m => ({ default: m.CascadeListPage })));
const RegNodeListPage = React.lazy(() => import('./pages/regnodes').then(m => ({ default: m.RegNodeListPage })));
const SettingListPage = React.lazy(() => import('./pages/settings').then(m => ({ default: m.SettingListPage })));
const FormTemplateListPage = React.lazy(() => import('./pages/form-templates').then(m => ({ default: m.FormTemplateListPage })));

// Config Browser Pages
const BriefingTemplateListPage = React.lazy(() => import('./pages/briefing-templates').then(m => ({ default: m.BriefingTemplateListPage })));
const GovernanceListPage = React.lazy(() => import('./pages/governance').then(m => ({ default: m.GovernanceListPage })));
const UiSchemaListPage = React.lazy(() => import('./pages/ui-schemas').then(m => ({ default: m.UiSchemaListPage })));
const LensCatalogPage = React.lazy(() => import('./pages/lenses').then(m => ({ default: m.LensCatalogPage })));
const TenantBrandingAdminPage = React.lazy(() => import('./pages/branding').then(m => ({ default: m.TenantBrandingAdminPage })));
const DashboardSchemaListPage = React.lazy(() => import('./pages/dashboard-schemas').then(m => ({ default: m.DashboardSchemaListPage })));
const FormSchemaOverrideListPage = React.lazy(() => import('./pages/form-schema-overrides').then(m => ({ default: m.FormSchemaOverrideListPage })));
const EdiTemplateListPage = React.lazy(() => import('./pages/edi-templates').then(m => ({ default: m.EdiTemplateListPage })));

// Backend feature pages
const FacilityOnboardingPage = React.lazy(() => import('./pages/onboarding').then(m => ({ default: m.FacilityOnboardingPage })));
const RegulatorySourceMonitorPage = React.lazy(() => import('./pages/regulatory-sources').then(m => ({ default: m.RegulatorySourceMonitorPage })));
const InstanceCleanupLogPage = React.lazy(() => import('./pages/instance-cleanup').then(m => ({ default: m.InstanceCleanupLogPage })));

// Gantt View (DRR-070)
const GanttPage = React.lazy(() => import('./pages/gantt').then(m => ({ default: m.GanttPage })));

// Calendar View
const CalendarPage = React.lazy(() => import('./pages/calendar/CalendarPage').then(m => ({ default: m.CalendarPage })));
const MySchedulePage = React.lazy(() => import('./pages/calendar/MySchedulePage').then(m => ({ default: m.MySchedulePage })));

// Workflow Orchestration (DRR-072)
const WorkflowOrchestrationPage = React.lazy(() => import('./pages/workflow-orchestration').then(m => ({ default: m.WorkflowOrchestrationPage })));

// Data Source Browser (DRR-072)
const DataSourceBrowserPage = React.lazy(() => import('./pages/data-sources').then(m => ({ default: m.DataSourceBrowserPage })));

// Intake Studio (DRR-137)
const IntakeStudioPage = React.lazy(() => import('./pages/document-studio').then(m => ({ default: m.IntakeStudioPage })));

// PDF Studio (DRR-079)
const PdfStudioPage = React.lazy(() => import('./pages/pdf-studio').then(m => ({ default: m.PdfStudioPage })));

// Employee-Facing Pages (Layer 1)
const MyInboxPage = React.lazy(() => import('./pages/inbox').then(m => ({ default: m.MyInboxPage })));
const MyDocumentsPage = React.lazy(() => import('./pages/documents').then(m => ({ default: m.MyDocumentsPage })));
const SigningCeremonyPage = React.lazy(() => import('./pages/documents/SigningCeremonyPage').then(m => ({ default: m.SigningCeremonyPage })));
const PublicSigningPage = React.lazy(() => import('./pages/signing/PublicSigningPage').then(m => ({ default: m.PublicSigningPage })));
const UsrnPortalPage = React.lazy(() => import('./pages/usrn-portal').then(m => ({ default: m.UsrnPortalPage })));
const MobileClockInPage = React.lazy(() => import('./pages/mobile/MobileClockInPage').then(m => ({ default: m.MobileClockInPage })));
const DocumentViewerPage = React.lazy(() => import('./pages/documents/DocumentViewerPage').then(m => ({ default: m.DocumentViewerPage })));
const WorkflowInstanceProgress = React.lazy(() => import('./pages/documents/components/WorkflowInstanceProgress').then(m => ({ default: m.WorkflowInstanceProgress })));
const ApprovalListPage = React.lazy(() => import('./pages/approvals').then(m => ({ default: m.ApprovalListPage })));
const ApprovalInboxPage = React.lazy(() => import('./pages/approvals').then(m => ({ default: m.ApprovalInboxPage })));
const MyProfilePage = React.lazy(() => import('./pages/profile').then(m => ({ default: m.MyProfilePage })));

// Operations Pages (DRR-233)
const ScheduleCalendarPage = React.lazy(() => import('./pages/operations').then(m => ({ default: m.ScheduleCalendarPage })));
const IntegrationStatusPage = React.lazy(() => import('./pages/operations').then(m => ({ default: m.IntegrationStatusPage })));

// Unified Primitive Form (25-primitive management)
const UnifiedPrimitivePage = React.lazy(() => import('./pages/primitives').then(m => ({ default: m.UnifiedPrimitivePage })));

// Team Workspace + Management (DRR-061)
const TeamWorkspacePage = React.lazy(() => import('./pages/team').then(m => ({ default: m.TeamWorkspacePage })));
const TeamManagementPage = React.lazy(() => import('./pages/team').then(m => ({ default: m.TeamManagementPage })));

// Workforce Hub
const WorkforcePage = React.lazy(() => import('./pages/workforce').then(m => ({ default: m.WorkforcePage })));
const ActorDetailPage = React.lazy(() => import('./pages/workforce').then(m => ({ default: m.ActorDetailPage })));
const TeamDetailPage = React.lazy(() => import('./pages/workforce').then(m => ({ default: m.TeamDetailPage })));

// Constellation Visualization (DRR-085, DRR-090)
const ConstellationPage = React.lazy(() => import('./pages/constellation').then(m => ({ default: m.ConstellationPage })));

// Care Journey (DRR-126, DRR-309)
const JourneyPage = React.lazy(() => import('./pages/journey').then(m => ({ default: m.JourneyPage })));
const JourneyViewPage = React.lazy(() => import('./pages/journey').then(m => ({ default: m.JourneyViewPage })));

// Care Plan Pages (DRR-220)
const DonReviewPage = React.lazy(() => import('./pages/care-plan').then(m => ({ default: m.DonReviewPage })));
const DelegatedAssessmentPage = React.lazy(() => import('./pages/workflow/DelegatedAssessmentPage').then(m => ({ default: m.DelegatedAssessmentPage })));

// Coordination Engine (DRR-211, DRR-222)
const ShiftPlanPage = React.lazy(() => import('./pages/coordination').then(m => ({ default: m.ShiftPlanPage })));
const FlowMapPage = React.lazy(() => import('./pages/coordination').then(m => ({ default: m.FlowMapPage })));

// Domain-Agnostic Plan Pages (DRR-232, DRR-303)
const ShiftWorkPage = React.lazy(() => import('./pages/plans').then(m => ({ default: m.ShiftWorkPage })));
const PlanApprovalQueuePage = React.lazy(() => import('./pages/plans').then(m => ({ default: m.PlanApprovalQueuePage })));
const ControlledInventoryPage = React.lazy(() => import('./pages/plans').then(m => ({ default: m.ControlledInventoryPage })));

// Person-Centered View (DRR-134/135)
const MyPeoplePage = React.lazy(() => import('./pages/people/MyPeoplePage').then(m => ({ default: m.MyPeoplePage })));

// Ledger Pages (DRR-109)
const LedgerDashboardPage = React.lazy(() => import('./pages/ledger').then(m => ({ default: m.LedgerDashboardPage })));
const LedgerFamilyPage = React.lazy(() => import('./pages/ledger').then(m => ({ default: m.LedgerFamilyPage })));
const LedgerAuditPage = React.lazy(() => import('./pages/ledger').then(m => ({ default: m.LedgerAuditPage })));

// Scope DAG Visualization (DRR-075)
const ScopeHierarchyPage = React.lazy(() => import('./pages/scope/ScopeHierarchyPage').then(m => ({ default: m.ScopeHierarchyPage })));

// My Access (DRR-102)
const MyAccessPage = React.lazy(() => import('./pages/scope/MyAccessPage').then(m => ({ default: m.MyAccessPage })));

// CFSS Pages (DRR-279)
const CfssDashboardPage = React.lazy(() => import('./pages/cfss').then(m => ({ default: m.CfssDashboardPage })));
const CfssTimesheetApprovalPage = React.lazy(() => import('./pages/cfss').then(m => ({ default: m.CfssTimesheetApprovalPage })));
const CfssSupervisionPage = React.lazy(() => import('./pages/cfss').then(m => ({ default: m.CfssSupervisionPage })));
const CfssPayrollPage = React.lazy(() => import('./pages/cfss').then(m => ({ default: m.CfssPayrollPage })));
const CfssWorkerManagementPage = React.lazy(() => import('./pages/cfss').then(m => ({ default: m.CfssWorkerManagementPage })));
const CfssServiceAuthPage = React.lazy(() => import('./pages/cfss').then(m => ({ default: m.CfssServiceAuthPage })));
const CfssTrainingPage = React.lazy(() => import('./pages/cfss').then(m => ({ default: m.CfssTrainingPage })));
const CfssDocumentsPage = React.lazy(() => import('./pages/cfss').then(m => ({ default: m.CfssDocumentsPage })));
const CfssIncidentPage = React.lazy(() => import('./pages/cfss').then(m => ({ default: m.CfssIncidentPage })));
const CfssCommunicationLogPage = React.lazy(() => import('./pages/cfss').then(m => ({ default: m.CfssCommunicationLogPage })));

// Self-service pages
const MyTrainingPage = React.lazy(() => import('./pages/training/MyTrainingPage').then(m => ({ default: m.MyTrainingPage })));
const StaffTrainingPage = React.lazy(() => import('./pages/training/StaffTrainingPage').then(m => ({ default: m.StaffTrainingPage })));
const MyPayrollPage = React.lazy(() => import('./pages/payroll').then(m => ({ default: m.MyPayrollPage })));
const PayrollDashboardPage = React.lazy(() => import('./pages/payroll').then(m => ({ default: m.PayrollDashboardPage })));
const TaxNotificationsPage = React.lazy(() => import('./pages/payroll').then(m => ({ default: m.TaxNotificationsPage })));

// Guardian Pages (DRR-199)
const GuardianDashboardPage = React.lazy(() => import('./pages/guardian').then(m => ({ default: m.GuardianDashboardPage })));
const GuardianCompliancePage = React.lazy(() => import('./pages/guardian').then(m => ({ default: m.GuardianCompliancePage })));

// Cross-Tenant Exchange (DRR-105)
const ExchangePage = React.lazy(() => import('./pages/exchange').then(m => ({ default: m.ExchangePage })));

// Enrollment (FERP-PII-052)
const EnrollmentPage = React.lazy(() => import('./pages/EnrollmentPage').then(m => ({ default: m.EnrollmentPage })));

// PII Field Demo (FERP-PII-052)
const L1FieldDemo = React.lazy(() => import('./pii').then(m => ({ default: m.L1FieldDemo })));

// PII Capture Page (DRR-302)
const PiiCapturePage = React.lazy(() => import('./pages/pii').then(m => ({ default: m.PiiCapturePage })));

// Subgraph Entity Form Pages (DRR-055)
const SubgraphEntityCreatePage = React.lazy(() => import('./pages/subgraphs/SubgraphEntityFormPage').then(m => ({ default: m.SubgraphEntityCreatePage })));
const SubgraphEntityEditPage = React.lazy(() => import('./pages/subgraphs/SubgraphEntityFormPage').then(m => ({ default: m.SubgraphEntityEditPage })));
const SubgraphEntityViewPage = React.lazy(() => import('./pages/subgraphs/SubgraphEntityFormPage').then(m => ({ default: m.SubgraphEntityViewPage })));
const SubgraphEntityApprovePage = React.lazy(() => import('./pages/subgraphs/SubgraphEntityFormPage').then(m => ({ default: m.SubgraphEntityApprovePage })));

// Universal Subgraph Browser (DRR-055)
const SubgraphBrowserPage = React.lazy(() => import('./pages/subgraphs').then(m => ({ default: m.SubgraphBrowserPage })));

// Tenant Setup Wizard (DRR-073)
const TenantSetupWizard = React.lazy(() => import('./features/tenant-setup/TenantSetupWizard').then(m => ({ default: m.TenantSetupWizard })));

// Tenant Management (DRR-073)
const TenantOnboardingPage = React.lazy(() => import('./pages/tenant-management').then(m => ({ default: m.TenantManagementPage })));

// Legacy Archive Browser (DRR-092)
const LegacyArchivePage = React.lazy(() => import('./pages/legacy-archive/LegacyArchivePage').then(m => ({ default: m.LegacyArchivePage })));

// Component Explorer
const ComponentExplorerPage = React.lazy(() => import('./pages/explorer').then(m => ({ default: m.ComponentExplorerPage })));

// Dev Dashboard
const DevDashboardPage = React.lazy(() => import('./pages/dev/DevDashboardPage').then(m => ({ default: m.DevDashboardPage })));

// Registry Explorer (DRR-111)
const RegistryExplorerPage = React.lazy(() => import('./pages/architect/RegistryExplorerPage').then(m => ({ default: m.RegistryExplorerPage })));

// Architect Pages (DRR-172)
const StatuteIngestionPage = React.lazy(() => import('./pages/architect/StatuteIngestionPage').then(m => ({ default: m.StatuteIngestionPage })));
const ProposalReviewPage = React.lazy(() => import('./pages/architect/ProposalReviewPage').then(m => ({ default: m.ProposalReviewPage })));
const ProductBuilderPage = React.lazy(() => import('./pages/architect/ProductBuilderPage').then(m => ({ default: m.ProductBuilderPage })));

// Workflow Observer
const WorkflowObserverPage = React.lazy(() => import('./pages/architect/WorkflowObserverPage'));
const WorkflowFlowPage = React.lazy(() => import('./pages/workflows/WorkflowFlowPage'));
const DslEditorPage = React.lazy(() => import('./pages/architect/DslEditorPage'));

// Ambient Operations Display (DRR-183)
const AmbientDisplayPage = React.lazy(() => import('./pages/AmbientDisplayPage').then(m => ({ default: m.AmbientDisplayPage })));

// Role-based dashboards
const StaffDashboard = React.lazy(() => import('./pages/dashboards/StaffDashboard').then(m => ({ default: m.StaffDashboard })));
const SupervisorDashboard = React.lazy(() => import('./pages/dashboards/SupervisorDashboard').then(m => ({ default: m.SupervisorDashboard })));
const StaffDashboardPage = React.lazy(() => import('./pages/dashboards').then(m => ({ default: m.StaffDashboardPage })));
const SupervisorDashboardPage = React.lazy(() => import('./pages/dashboards').then(m => ({ default: m.SupervisorDashboardPage })));

// ============================================================================
// PAGE LOADING FALLBACK - Shows while lazy pages load
// ============================================================================

function PageLoader() {
  return (
    <Center h="50vh">
      <Stack ta="center" gap="md">
        <Loader size="lg" type="dots" />
        <Text size="sm" c="dimmed">Loading...</Text>
      </Stack>
    </Center>
  );
}

/**
 * Tenant Setup Page (DRR-073)
 * Wraps TenantSetupWizard with auth context to pass tenantId and navigation.
 */
function TenantSetupPage() {
  const { tenant } = useAuth();
  const navigate = useNavigate();

  return (
    <Suspense fallback={<PageLoader />}>
      <TenantSetupWizard
        tenantId={tenant?.id ?? 'unknown'}
        onComplete={() => navigate('/ferp-core/dashboard')}
        onCancel={() => navigate('/ferp-core/dashboard')}
      />
    </Suspense>
  );
}

/**
 * Protected Route wrapper - requires authentication
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Center h="100vh">
        <Stack ta="center" gap="md">
          <Loader size="xl" type="dots" />
          <Text size="sm" c="dimmed">Authenticating...</Text>
        </Stack>
      </Center>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// DRR-256: SubjectDashboardPage is available at /ferp-core/subjects as a dedicated
// person-centered view. The main dashboard remains unchanged for all view layers.

// ViewLayerSync moved to NavigationContext (DRR-313) to eliminate query cascade

/**
 * Protected Routes with Dynamic Subgraph Routes
 *
 * Renders core static routes + dynamic subgraph routes from config.
 * This component must be inside AuthProvider to access tenant context.
 */
function ProtectedAppRoutes() {
  const { tenant } = useAuth();

  // DATA-DRIVEN: Get tenant's subgraph configuration
  const { config } = useTenantSubgraphConfig(tenant?.id ?? null);

  // Generate dynamic routes from SubgraphMetadata
  const dynamicRoutes = useDynamicRoutes(config);

  // Terminology: translates FERP primitive names to industry-specific labels
  const { branding } = useAdaptiveTenantBranding(tenant?.id ?? null);

  // DRR-189: Build flat concept→display map from resolved terminology.
  // This feeds TerminologyProvider so all useTerminology().translate() calls
  // get statutory-backed terminology instead of manually-authored labels.
  const { terms: resolvedTerms } = useResolvedTerminology();
  const terminologyMap = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    // Add branding terminology
    if (branding?.terminology) {
      for (const [key, value] of Object.entries(branding.terminology)) {
        map[key] = value;
      }
    }
    // Merge resolved terms (DRR-189 statute-backed takes priority)
    if (resolvedTerms) {
      for (const [concept, term] of Object.entries(resolvedTerms)) {
        map[concept] = term.display;
      }
    }
    return map;
  }, [branding, resolvedTerms]);

  return (
    <TerminologyProvider terminologyMap={terminologyMap} branding={branding}>
      <FhirLabelsProvider>
        <ActorNameProvider>
          <ViewContextProvider>
            <NavigationProvider>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* =============================================================== */}
                  {/* Ambient Display — FULL-SCREEN, no AppShell (DRR-183)            */}
                  {/* =============================================================== */}
                  <Route path="/ambient" element={<AmbientDisplayPage />} />

                  {/* =============================================================== */}
                  {/* Standard routes with MainLayout wrapper                         */}
                  {/* =============================================================== */}
                  <Route element={<MainLayout />}>
                    {/* Dashboard - Default entry point */}
                    <Route path="/" element={<Navigate to="/ferp-core/dashboard" replace />} />
                    <Route path="/ferp-core/dashboard" element={<DashboardPage />} />
                    <Route path="/ferp-core/workflows/:workflowId/visualize" element={<WorkflowVisualizerPage />} />
                    <Route path="/ferp-core/work-surface" element={<WorkSurfacePageConnected />} />

                    {/* DRR-256: Subject Dashboard — person-centered view for care staff */}
                    <Route path="/ferp-core/subjects" element={<SubjectDashboardPage />} />
                    <Route path="/ferp-core/subjects/:subjectId" element={<SubjectDashboardPage />} />

                    {/* Profile page for self-service */}
                    <Route path="/profile" element={<MyProfilePage />} />
                    <Route path="/me/profile" element={<MyProfilePage />} />

                    {/* ============================================================= */}
                    {/* DESIGN Phase Primitives (Architect Layer)                     */}
                    {/* ============================================================= */}
                    <Route path="/architect/workflows" element={<WorkflowListPage />} />
                    <Route path="/architect/workflows/studio" element={<WorkflowStudioPage />} />
                    <Route path="/architect/workflows/registry" element={<WorkflowRegistryPage />} />
                    <Route path="/architect/workflows/llm-editor" element={<LlmWorkflowEditorPage />} />
                    <Route path="/architect/workflows/master" element={<MasterWorkflowPage />} />
                    <Route path="/architect/workflows/:workflowId" element={<WorkflowDetailPage />} />
                    <Route path="/architect/workflows/:workflowId/flow" element={<WorkflowFlowPage />} />
                    <Route path="/ferp-core/forms" element={<FormGalleryPage />} />
                    <Route path="/architect/forms" element={<FormGalleryPage />} />
                    <Route path="/architect/forms/llm-editor" element={<LlmFormSchemaEditorPage />} />
                    <Route path="/architect/constraints" element={<ConstraintListPage />} />
                    <Route path="/architect/dependencies" element={<DependencyListPage />} />
                    <Route path="/architect/templates" element={<TemplateListPage />} />

                    {/* Architect Pages (DRR-172) */}
                    <Route path="/architect/registry" element={<RegistryExplorerPage />} />
                    <Route path="/architect/statute-ingestion" element={<StatuteIngestionPage />} />
                    <Route path="/architect/proposals" element={<ProposalReviewPage />} />
                    <Route path="/architect/products" element={<ProductBuilderPage />} />
                    <Route path="/architect/observer" element={<WorkflowObserverPage />} />
                    <Route path="/architect/dsl" element={<DslEditorPage />} />

                    {/* ============================================================= */}
                    {/* CONFIGURE Phase Primitives (Admin Layer)                      */}
                    {/* ============================================================= */}
                    <Route path="/admin/actors" element={<ActorManagementPage />} />
                    <Route path="/admin/actors/list" element={<ActorListPage />} />
                    <Route path="/admin/policies" element={<PolicyListPage />} />
                    <Route path="/admin/roles" element={<RoleListPage />} />
                    <Route path="/admin/schedules" element={<ScheduleListPage />} />
                    <Route path="/admin/queues" element={<QueueListPage />} />
                    <Route path="/admin/validations" element={<ValidationListPage />} />

                    {/* Admin Pages (bulk) */}
                    <Route path="/admin/form-schemas" element={<FormSchemaAdminPage />} />
                    <Route path="/admin/primitive-constellation" element={<PrimitiveConstellationPage />} />
                    <Route path="/admin/source-maps" element={<SourceMapListPage />} />
                    <Route path="/admin/source-maps/:mapId" element={<SourceMapReviewPage />} />
                    <Route path="/admin/products" element={<ProductCatalogPage />} />
                    <Route path="/admin/role-comparison" element={<RoleAccessComparisonPage />} />
                    <Route path="/admin/rbac" element={<RBACDashboardPage />} />
                    <Route path="/admin/auth-explorer" element={<AuthExplorerPage />} />
                    <Route path="/admin/witness-queue" element={<WitnessQueuePage />} />
                    <Route path="/admin/sagas" element={<SagaMonitoringPage />} />
                    <Route path="/admin/executive-safety" element={<ExecutiveSafetyPage />} />
                    <Route path="/admin/cache" element={<CacheManagementPage />} />
                    <Route path="/admin/c-level-audit" element={<CLevelAuditPage />} />
                    <Route path="/admin/jobs" element={<JobMonitoringPage />} />
                    <Route path="/admin/queue-manager" element={<QueueManagerPage />} />
                    <Route path="/admin/system-health" element={<SystemHealthPage />} />
                    <Route path="/admin/optimization" element={<OptimizationPage />} />
                    <Route path="/admin/task-analytics" element={<TaskAnalyticsPage />} />
                    <Route path="/admin/process-analytics" element={<ProcessAnalyticsPage />} />
                    <Route path="/admin/approval-queue" element={<ApprovalQueuePage />} />
                    <Route path="/admin/emergency" element={<EmergencyParticipantPage />} />
                    <Route path="/admin/safety-events" element={<SafetyEventsPage />} />
                    <Route path="/admin/validation-review" element={<ValidationReviewPage />} />
                    <Route path="/admin/llm-comparison" element={<MultiLLMComparisonPage />} />
                    <Route path="/admin/policy-analytics" element={<PolicyAnalyticsPage />} />
                    <Route path="/admin/regulatory-governance" element={<RegulatoryGovernancePage />} />
                    <Route path="/admin/production-monitoring" element={<ProductionMonitoringPage />} />
                    <Route path="/admin/workflow-composition" element={<WorkflowCompositionPage />} />
                    <Route path="/admin/actor-runtime" element={<ActorRuntimePage />} />
                    <Route path="/admin/actor-descriptors" element={<ActorDescriptorPage />} />
                    <Route path="/admin/drafts" element={<DraftManagementPage />} />
                    <Route path="/admin/comprehensive" element={<ComprehensiveDashboardPage />} />
                    <Route path="/admin/secure-health" element={<SecureSystemHealthPage />} />
                    <Route path="/admin/tabulator-lifecycle" element={<TabulatorLifecyclePage />} />
                    <Route path="/admin/primitive-composition" element={<PrimitiveCompositionPage />} />
                    <Route path="/admin/primitive-registry" element={<PrimitiveRegistryPage />} />
                    <Route path="/admin/domain-config" element={<DomainConfigPage />} />
                    <Route path="/admin/compliance-config" element={<ComplianceConfigPage />} />
                    <Route path="/admin/governance-dashboard" element={<GovernanceDashboardPage />} />
                    <Route path="/admin/governance-context" element={<GovernanceContextPage />} />
                    <Route path="/admin/saga-metrics" element={<SagaMetricsPage />} />
                    <Route path="/admin/monitoring" element={<MonitoringDashboardPage />} />
                    <Route path="/admin/role-management" element={<RoleManagementPage />} />
                    <Route path="/admin/integration-management" element={<IntegrationManagementPage />} />
                    <Route path="/admin/resource-management" element={<ResourceManagementPage />} />
                    <Route path="/admin/escalation-management" element={<EscalationManagementPage />} />
                    <Route path="/admin/schedule-management" element={<ScheduleManagementPage />} />
                    <Route path="/admin/policy-management" element={<PolicyManagementPage />} />
                    <Route path="/admin/expert-routing" element={<ExpertRoutingPage />} />
                    <Route path="/admin/task-approval" element={<TaskApprovalPage />} />
                    <Route path="/admin/approval-policies" element={<ApprovalPoliciesPage />} />
                    <Route path="/admin/tabulator-entity" element={<TabulatorEntityPage />} />
                    <Route path="/admin/master-workflow" element={<MasterWorkflowDashboardPage />} />
                    <Route path="/admin/template-workflow" element={<TemplateWorkflowPage />} />
                    <Route path="/admin/conversion-progress" element={<ConversionProgressPage />} />
                    <Route path="/admin/extraction-review" element={<ExtractionReviewPage />} />
                    <Route path="/admin/enhanced" element={<DashboardEnhancedPage />} />
                    <Route path="/admin/graphql-widgets" element={<GraphQLWidgetsPage />} />
                    <Route path="/admin/lifecycle-widgets" element={<LifecycleWidgetsPage />} />
                    <Route path="/admin/composition-engine" element={<CompositionEnginePage />} />
                    <Route path="/admin/complex-process" element={<ComplexProcessPage />} />
                    <Route path="/admin/performance" element={<PerformanceOptimizationPage />} />
                    <Route path="/admin/chaos-template" element={<ChaosTemplatePage />} />
                    <Route path="/admin/sprint7" element={<Sprint7DashboardPage />} />
                    <Route path="/admin/ambient" element={<AmbientDashboardPage />} />
                    <Route path="/admin/qapi" element={<QapiDashboardPage />} />
                    <Route path="/admin/compliance-dashboard" element={<ComplianceDashboardPage />} />
                    <Route path="/admin/prod-health" element={<ProductionSystemHealthPage />} />
                    <Route path="/admin/config-proposals" element={<ConfigProposalQueuePage />} />
                    <Route path="/admin/search" element={<SearchPage />} />
                    <Route path="/admin/terminology" element={<TerminologyPage />} />
                    <Route path="/admin/auth-observability" element={<AuthObservabilityPage />} />
                    <Route path="/admin/entity-types" element={<EntityTypesPage />} />
                    <Route path="/admin/subgraph/:subgraphId" element={<SubgraphEntityManagerPage />} />
                    <Route path="/admin/spatial-pinning" element={<SpatialPinningEditor />} />

                    {/* ============================================================= */}
                    {/* EXECUTE Phase Primitives (User Layer)                         */}
                    {/* ============================================================= */}
                    <Route path="/ferp-core/tasks" element={<TaskListPage />} />
                    <Route path="/ferp-core/tasks/:taskId" element={<TaskDetailPage />} />
                    <Route path="/ferp-core/task-templates" element={<TaskTemplatesPage />} />
                    <Route path="/ferp-core/task-assignments" element={<TaskAssignmentsPage />} />
                    <Route path="/ferp-core/overdue-tasks" element={<OverdueTasksPage />} />
                    <Route path="/ferp-core/task-delegation" element={<TaskDelegationPage />} />
                    <Route path="/ferp-core/states" element={<StateListPage />} />
                    <Route path="/ferp-core/events" element={<EventListPage />} />
                    <Route path="/ferp-core/sagas" element={<SagaListPage />} />
                    <Route path="/ferp-core/dagnodes" element={<DAGNodeListPage />} />
                    <Route path="/ferp-core/instances" element={<InstanceListPage />} />
                    <Route path="/ferp-core/instances/:instanceId" element={<InstanceDetailPage />} />

                    {/* Workflow State View (DRR-309 pattern dispatch) */}
                    <Route path="/ferp-core/workflow-state/:instanceId" element={<WorkflowStatePage />} />
                    {/* Legacy Kanban view (direct access) */}
                    <Route path="/ferp-core/workflow-kanban/:instanceId" element={<WorkflowStateView />} />

                    {/* ============================================================= */}
                    {/* MONITOR Phase Primitives                                      */}
                    {/* ============================================================= */}
                    <Route path="/ferp-core/metrics" element={<MetricListPage />} />
                    <Route path="/ferp-core/alerts" element={<AlertListPage />} />
                    <Route path="/alerts/beacon" element={<BeaconDashboardPage />} />
                    <Route path="/ferp-core/reports" element={<ReportListPage />} />
                    <Route path="/ferp-core/notifications" element={<NotificationListPage />} />
                    <Route path="/ferp-core/transactions" element={<TransactionListPage />} />
                    <Route path="/ferp-core/traces" element={<TraceListPage />} />
                    <Route path="/ferp-core/checkpoints" element={<CheckpointListPage />} />

                    {/* ============================================================= */}
                    {/* GOVERN Phase Primitives                                       */}
                    {/* ============================================================= */}
                    <Route path="/ferp-core/compliance" element={<ComplianceListPage />} />
                    <Route path="/ferp-core/compliance/readiness" element={<ReadinessDashboard />} />
                    <Route path="/ferp-core/compliance/evidence" element={<ComplianceEvidencePage />} />
                    <Route path="/ferp-core/compliance/survey" element={<PracticeSurveyRunner />} />
                    <Route path="/ferp-core/compliance/photo" element={<PhotoComplianceUpload />} />
                    <Route path="/ferp-core/rulesets" element={<RuleSetViewerPage />} />
                    <Route path="/ferp-core/compliance/ruleset/:rulesetId" element={<RuleSetViewerPage />} />
                    <Route path="/ferp-core/compliance/ruleset-admin" element={<RuleSetAdminPage />} />
                    <Route path="/ferp-core/compliance/standards-packs" element={<StandardsPackPage />} />
                    <Route path="/ferp-core/compliance/assessment-evidence" element={<AssessmentEvidenceDashboard />} />
                    <Route path="/ferp-core/compliance/manual" element={<ComplianceManualPage />} />
                    <Route path="/ferp-core/compliance/audit-forms" element={<AuditFormsPage />} />
                    <Route path="/ferp-core/risks" element={<RiskListPage />} />
                    <Route path="/ferp-core/evidence" element={<EvidenceListPage />} />
                    <Route path="/ferp-core/contracts" element={<ContractListPage />} />

                    {/* ============================================================= */}
                    {/* OPTIMIZE Phase Primitives                                     */}
                    {/* ============================================================= */}
                    <Route path="/ferp-core/audits" element={<AuditListPage />} />
                    <Route path="/ferp-core/analytics" element={<AnalyticsListPage />} />
                    <Route path="/ferp-core/reconciliation" element={<ReconciliationDashboardPage />} />
                    <Route path="/ferp-core/resources" element={<ResourceListPage />} />
                    <Route path="/ferp-core/integrations" element={<IntegrationListPage />} />

                    {/* ============================================================= */}
                    {/* CONFIG TYPE PAGES                                             */}
                    {/* ============================================================= */}
                    <Route path="/ferp-core/task-groups" element={<TaskGroupListPage />} />
                    <Route path="/ferp-core/task-groups/:taskGroupId" element={<TaskGroupDetailPage />} />
                    <Route path="/ferp-core/task-groups/progress" element={<TaskGroupProgressPage />} />
                    <Route path="/ferp-core/cascades" element={<CascadeListPage />} />
                    <Route path="/ferp-core/regnodes" element={<RegNodeListPage />} />
                    <Route path="/ferp-core/settings" element={<SettingListPage />} />
                    <Route path="/ferp-core/form-templates" element={<FormTemplateListPage />} />
                    <Route path="/ferp-core/briefing-templates" element={<BriefingTemplateListPage />} />
                    <Route path="/ferp-core/governance" element={<GovernanceListPage />} />
                    <Route path="/ferp-core/ui-schemas" element={<UiSchemaListPage />} />
                    <Route path="/ferp-core/lenses" element={<LensCatalogPage />} />
                    <Route path="/ferp-core/branding" element={<TenantBrandingAdminPage />} />
                    <Route path="/ferp-core/dashboard-schemas" element={<DashboardSchemaListPage />} />
                    <Route path="/ferp-core/form-schema-overrides" element={<FormSchemaOverrideListPage />} />
                    <Route path="/ferp-core/edi-templates" element={<EdiTemplateListPage />} />

                    {/* ============================================================= */}
                    {/* FEATURE PAGES                                                 */}
                    {/* ============================================================= */}
                    <Route path="/ferp-core/onboarding" element={<FacilityOnboardingPage />} />
                    <Route path="/ferp-core/regulatory-sources" element={<RegulatorySourceMonitorPage />} />
                    <Route path="/ferp-core/instance-cleanup" element={<InstanceCleanupLogPage />} />
                    <Route path="/ferp-core/gantt" element={<GanttPage />} />
                    <Route path="/ferp-core/calendar" element={<CalendarPage />} />
                    <Route path="/ferp-core/my-schedule" element={<MySchedulePage />} />
                    <Route path="/ferp-core/orchestration" element={<WorkflowOrchestrationPage />} />
                    <Route path="/ferp-core/data-sources" element={<DataSourceBrowserPage />} />
                    <Route path="/ferp-core/intake" element={<IntakeStudioPage />} />
                    <Route path="/ferp-core/pdf-studio" element={<PdfStudioPage />} />
                    <Route path="/sync" element={<SyncDashboardPage />} />

                    {/* Employee-Facing Pages (Layer 1) */}
                    <Route path="/me/inbox" element={<MyInboxPage />} />
                    <Route path="/ferp-core/inbox" element={<MyInboxPage />} />
                    <Route path="/me/documents" element={<MyDocumentsPage />} />
                    <Route path="/me/signing/:documentId" element={<SigningCeremonyPage />} />
                    <Route path="/usrn" element={<UsrnPortalPage />} />
                    <Route path="/clock-in" element={<MobileClockInPage />} />
                    <Route path="/documents/:documentId" element={<DocumentViewerPage />} />
                    <Route path="/documents/:documentId/workflow" element={<WorkflowInstanceProgress />} />
                    <Route path="/ferp-core/approvals" element={<ApprovalListPage />} />
                    <Route path="/ferp-core/approvals/inbox" element={<ApprovalInboxPage />} />

                    {/* Operations Pages (DRR-233) */}
                    <Route path="/operations/schedule" element={<ScheduleCalendarPage />} />
                    <Route path="/operations/integrations" element={<IntegrationStatusPage />} />

                    {/* Unified Primitive Form */}
                    <Route path="/primitives/:primitiveType" element={<UnifiedPrimitivePage />} />
                    <Route path="/primitives/:primitiveType/:id" element={<UnifiedPrimitivePage />} />

                    {/* Team Workspace + Management */}
                    <Route path="/ferp-core/team" element={<TeamWorkspacePage />} />
                    <Route path="/ferp-core/team/manage" element={<TeamManagementPage />} />

                    {/* Workforce Hub */}
                    <Route path="/ferp-core/workforce" element={<WorkforcePage />} />
                    <Route path="/ferp-core/workforce/actors/:actorId" element={<ActorDetailPage />} />
                    <Route path="/ferp-core/workforce/teams/:teamId" element={<TeamDetailPage />} />

                    {/* Constellation Visualization */}
                    <Route path="/ferp-core/constellation" element={<ConstellationPage />} />
                    <Route path="/ferp-core/constellation/:constellationId" element={<ConstellationPage />} />

                    {/* Care Journey */}
                    <Route path="/ferp-core/journey" element={<JourneyPage />} />
                    <Route path="/ferp-core/journey/:instanceId" element={<JourneyViewPage />} />

                    {/* Care Plan Pages */}
                    <Route path="/ferp-core/care-plan/don-review" element={<DonReviewPage />} />
                    <Route path="/ferp-core/delegated-assessment/:workflowId" element={<DelegatedAssessmentPage />} />

                    {/* Coordination Engine */}
                    <Route path="/ferp-core/shift-plan" element={<ShiftPlanPage />} />
                    <Route path="/ferp-core/flow-map" element={<FlowMapPage />} />

                    {/* Domain-Agnostic Plan Pages */}
                    <Route path="/ferp-core/shift-work" element={<ShiftWorkPage />} />
                    <Route path="/ferp-core/plan-approval" element={<PlanApprovalQueuePage />} />
                    <Route path="/ferp-core/controlled-inventory" element={<ControlledInventoryPage />} />

                    {/* Person-Centered View */}
                    <Route path="/ferp-core/my-people" element={<MyPeoplePage />} />

                    {/* Ledger Pages */}
                    <Route path="/ferp-core/ledger" element={<LedgerDashboardPage />} />
                    <Route path="/ferp-core/ledger/family/:familyId" element={<LedgerFamilyPage />} />
                    <Route path="/ferp-core/ledger/audit" element={<LedgerAuditPage />} />

                    {/* Scope Pages */}
                    <Route path="/ferp-core/scope-hierarchy" element={<ScopeHierarchyPage />} />
                    <Route path="/ferp-core/my-access" element={<MyAccessPage />} />

                    {/* CFSS Pages */}
                    <Route path="/cfss" element={<CfssDashboardPage />} />
                    <Route path="/cfss/timesheets" element={<CfssTimesheetApprovalPage />} />
                    <Route path="/cfss/supervision" element={<CfssSupervisionPage />} />
                    <Route path="/cfss/payroll" element={<CfssPayrollPage />} />
                    <Route path="/cfss/workers" element={<CfssWorkerManagementPage />} />
                    <Route path="/cfss/authorizations" element={<CfssServiceAuthPage />} />
                    <Route path="/cfss/training" element={<CfssTrainingPage />} />
                    <Route path="/cfss/documents" element={<CfssDocumentsPage />} />
                    <Route path="/cfss/incidents" element={<CfssIncidentPage />} />
                    <Route path="/cfss/communication" element={<CfssCommunicationLogPage />} />

                    {/* Self-service pages */}
                    <Route path="/me/training" element={<MyTrainingPage />} />
                    <Route path="/admin/staff-training" element={<StaffTrainingPage />} />
                    <Route path="/me/payroll" element={<MyPayrollPage />} />
                    <Route path="/admin/payroll" element={<PayrollDashboardPage />} />
                    <Route path="/admin/tax-notifications" element={<TaxNotificationsPage />} />

                    {/* Guardian Pages */}
                    <Route path="/guardian" element={<GuardianDashboardPage />} />
                    <Route path="/guardian/compliance" element={<GuardianCompliancePage />} />

                    {/* Best Practice Intelligence */}
                    <Route path="/ferp-core/recommendations" element={<RecommendationsPage />} />

                    {/* Cross-Tenant Exchange */}
                    <Route path="/exchange" element={<ExchangePage />} />

                    {/* Enrollment */}
                    <Route path="/enrollment" element={<EnrollmentPage />} />

                    {/* PII Pages */}
                    <Route path="/pii-demo" element={<L1FieldDemo />} />
                    <Route path="/pii-capture" element={<PiiCapturePage />} />

                    {/* Subgraph Dashboards (DRR-321: Multi-subgraph product enablement) */}
                    <Route path="/fhir/*" element={<SubgraphBrowserPage />} />
                    <Route path="/wearables/*" element={<SubgraphBrowserPage />} />
                    <Route path="/ai/*" element={<SubgraphBrowserPage />} />
                    <Route path="/erp/*" element={<SubgraphBrowserPage />} />
                    <Route path="/portal/*" element={<SubgraphBrowserPage />} />
                    <Route path="/edi/*" element={<SubgraphBrowserPage />} />

                    {/* Subgraph Entity Form Pages */}
                    <Route path="/:subgraphId/create/:entityType" element={<SubgraphEntityCreatePage />} />
                    <Route path="/:subgraphId/edit/:entityType/:entityId" element={<SubgraphEntityEditPage />} />
                    <Route path="/:subgraphId/view/:entityType/:entityId" element={<SubgraphEntityViewPage />} />
                    <Route path="/:subgraphId/approve/:entityType/:entityId" element={<SubgraphEntityApprovePage />} />

                    {/* Universal Subgraph Browser */}
                    <Route path="/admin/:subgraphId" element={<SubgraphBrowserPage />} />
                    <Route path="/architect/:subgraphId" element={<SubgraphBrowserPage />} />

                    {/* Tenant Setup */}
                    <Route path="/setup" element={<TenantSetupPage />} />
                    <Route path="/tenant-onboarding" element={<TenantOnboardingPage />} />

                    {/* Legacy Archive */}
                    <Route path="/legacy-archive" element={<LegacyArchivePage />} />

                    {/* Component Explorer */}
                    <Route path="/explorer" element={<ComponentExplorerPage />} />

                    {/* Dev Dashboard */}
                    <Route path="/dev" element={<DevDashboardPage />} />

                    {/* Hiring Pipeline */}
                    <Route path="/hiring/applicant-status" element={<ApplicantStatusPage />} />

                    {/* Role-based dashboards */}
                    <Route path="/dashboards/staff" element={<StaffDashboard />} />
                    <Route path="/dashboards/staff-page" element={<StaffDashboardPage />} />
                    <Route path="/dashboards/supervisor" element={<SupervisorDashboard />} />
                    <Route path="/dashboards/supervisor-page" element={<SupervisorDashboardPage />} />

                    {/* Architect Guard */}
                    <Route path="/architect/*" element={<RequireArchitect><PrimitiveGrid /></RequireArchitect>} />

                    {/* Dynamic routes from SubgraphMetadata */}
                    {dynamicRoutes}

                    {/* Catch-all redirect */}
                    <Route path="*" element={<Navigate to="/ferp-core/dashboard" replace />} />
                  </Route>
                </Routes>
              </Suspense>
            </NavigationProvider>
          </ViewContextProvider>
        </ActorNameProvider>
      </FhirLabelsProvider>
    </TerminologyProvider>
  );
}

/**
 * Root App component with providers
 */
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SimulationProvider>
          <PageErrorBoundary>
            {/* ViewLayerSync moved to NavigationContext (DRR-313) */}
            <Routes>
              {/* Public routes - no lazy loading needed, they're small */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
              <Route path="/signing/:token" element={
                <Suspense fallback={<PageLoader />}>
                  <PublicSigningPage />
                </Suspense>
              } />

              {/* Protected routes with layout */}
              <Route path="/*" element={
                <ProtectedRoute>
                  <ProtectedAppRoutes />
                </ProtectedRoute>
              } />
            </Routes>
          </PageErrorBoundary>
        </SimulationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
